import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { Trash2 } from "lucide-react";
import { KINDS, autoBrk, fmtHours, hh, hm, hoursOf, num, rowSpan, toMin } from "@/components/diary/CrewTimeline";

// 421: úpravy party v deníku — na počítači panel zprava (jako Kontrola směny), na telefonu okno zespodu.
function SidePanel({ open, onClose, title, testid, children, footer }) {
  const mobile = useIsMobile();
  const body = (
    <>
      <div className="px-5 pt-5 pb-2 text-[16px] font-bold">{title}</div>
      <div className="px-5 pb-4 overflow-y-auto space-y-4 min-h-0">{children}</div>
      <div className="px-5 py-3 border-t border-border flex flex-wrap items-center justify-end gap-2 max-md:pb-[calc(12px+env(safe-area-inset-bottom,0px))]">{footer}</div>
    </>
  );
  if (mobile) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent sheet aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()} data-testid={testid}
          className="p-0 max-md:pb-0 gap-0 overflow-hidden grid grid-rows-[auto_minmax(0,1fr)_auto] max-h-[88vh]">
          <DialogTitle className="sr-only">{title}</DialogTitle>{body}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()} data-testid={testid}
        className="p-0 border-0 inset-y-3 right-3 h-auto w-[min(420px,calc(100vw-24px))] sm:max-w-none rounded-[24px] bg-card overflow-hidden grid grid-rows-[auto_minmax(0,1fr)_auto] gap-0">
        <SheetTitle className="sr-only">{title}</SheetTitle>{body}
      </SheetContent>
    </Sheet>
  );
}

const TIMES = (lo = 5 * 60, hi = 21 * 60) => Array.from({ length: (hi - lo) / 15 + 1 }, (_, k) => lo + k * 15);
function TimeSel({ value, onChange, label, testid, lo, hi }) {
  const opts = TIMES(lo, hi);
  const list = opts.includes(value) ? opts : [...opts, value].sort((a, b) => a - b);
  return (
    <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted-foreground min-w-0">{label}
      <select value={value} onChange={(e) => onChange(+e.target.value)} data-testid={testid}
        className="h-11 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-[16px] font-semibold text-foreground tabular-nums">
        {list.map((m) => <option key={m} value={m}>{hh(m)}</option>)}
      </select>
    </label>
  );
}
const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} aria-pressed={on} data-testid={testid}
    className={`h-9 px-3 rounded-full border text-[13px] font-semibold ${on ? "border-brand bg-brand/10 text-brand" : "border-border bg-card hover:bg-secondary"}`}>{children}</button>
);

// Člověk v partě (nebo „Všem v partě stejně“, když row == null a all == true)
export function CrewPanel({ open, row, all, count, rule, others = [], onClose, onSave, onRemove }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!open) return;
    const sp = row ? rowSpan(row, rule) : null;
    setF({ a: sp?.a ?? 7 * 60, b: sp?.b ?? 15 * 60 + 30, auto: row ? row.brk == null : true, brk: row?.brk ?? rule?.minutes ?? 30,
      at: sp?.at ?? toMin(rule?.start) ?? 720, err: "" });
  }, [open, JSON.stringify(row || {}), rule]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const len = f.auto ? autoBrk(rule, (f.b - f.a - (rule?.minutes ?? 30)) / 60) : f.brk;
  const hours = hoursOf(f.a, f.b, f.auto ? null : f.brk, rule);
  const at = len ? Math.min(Math.max(f.at, f.a), f.b - len) : null;
  const otherH = others.reduce((s, x) => s + (x.hours || 0), 0);
  const save = () => {
    if (f.b - f.a < 30) return setF({ ...f, err: "Konec musí být aspoň 30 minut po začátku." });
    const clash = others.find((o) => o.a != null && f.a < o.b && f.b > o.a);
    if (clash && !all) return setF({ ...f, err: `Překrývá se s ${clash.project} ${hm(clash.a)}–${hm(clash.b)}.` });
    onSave({ start: hh(f.a), hours: Math.round(hours * 4) / 4, brk: f.auto ? null : f.brk, brkAt: len && !f.auto ? hh(at) : (row?.brkAt && len ? hh(at) : null) });
  };
  return (
    <SidePanel open={open} onClose={onClose} testid="crew-panel"
      title={all ? `Všem v partě stejně · ${count}` : `${row?.name || ""} · parta`}
      footer={<>
        {!all && <Button variant="outline" onClick={onRemove} className="h-11 mr-auto text-destructive hover:text-destructive" data-testid="crew-panel-remove"><Trash2 size={15} /> Odstranit z deníku</Button>}
        <Button variant="outline" onClick={onClose} className="h-11 max-md:flex-1">Zrušit</Button>
        <Button onClick={save} className="h-11 max-md:flex-1" data-testid="crew-panel-save">{all ? "Nastavit všem" : "Uložit"}</Button>
      </>}>
      <div className="grid grid-cols-2 gap-3">
        <TimeSel label="Od" value={f.a} onChange={(a) => setF({ ...f, a, err: "" })} testid="crew-panel-from" />
        <TimeSel label="Do" value={f.b} onChange={(b) => setF({ ...f, b, err: "" })} testid="crew-panel-to" />
      </div>
      <div>
        <div className="text-[12.5px] font-semibold text-muted-foreground mb-1.5">Přestávka</div>
        <div className="flex flex-wrap gap-1.5">
          <Chip on={f.auto} onClick={() => setF({ ...f, auto: true })} testid="crew-panel-brk-auto">auto · {autoBrk(rule, (f.b - f.a - (rule?.minutes ?? 30)) / 60)} min</Chip>
          {[0, 15, 30, 45, 60].map((x) => <Chip key={x} on={!f.auto && f.brk === x} onClick={() => setF({ ...f, auto: false, brk: x })} testid={`crew-panel-brk-${x}`}>{x} min</Chip>)}
        </div>
        {len > 0 && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <TimeSel label="Přestávka od" value={at} lo={f.a} hi={Math.max(f.a, f.b - len)} onChange={(v) => setF({ ...f, at: v, auto: false, brk: len })} testid="crew-panel-brk-at" />
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted-foreground">Do
              <span className="h-11 rounded-[12px] border border-border bg-secondary/50 px-3 flex items-center text-[16px] font-semibold text-foreground tabular-nums">{hh(at + len)}</span></label>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <b className="text-[18px] tabular-nums" data-testid="crew-panel-hours">{fmtHours(Math.round(hours * 4) / 4)}{all ? " každému" : " tady"}</b>
        {!all && otherH > 0 && <span className="text-[12.5px] text-muted-foreground tabular-nums">celkem za den {fmtHours(hours + otherH)}</span>}
      </div>
      {all && <p className="text-[12.5px] text-muted-foreground">Kdo byl ten den i na jiné zakázce a čas by se překrýval, zůstane beze změny.</p>}
      {f.err && <p className="text-sm font-semibold text-destructive" data-testid="crew-panel-err">{f.err}</p>}
    </SidePanel>
  );
}

// Člověk mimo tým — jen v zápisu deníku a v PDF, do docházky ani nákladů se nepíše
export function ManualPanel({ open, item, onClose, onSave, onRemove }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!open) return;
    const a = toMin(item?.start), b = toMin(item?.end);
    setF({ name: item?.name || "", role: item?.role || "", kind: item?.kind || "sub", timed: item?.isNew ? true : a != null,
      a: a ?? 8 * 60, b: b ?? 12 * 60, hours: item?.hours ?? "", err: "" });
  }, [open, JSON.stringify(item || {})]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const isNew = item?.isNew;
  const save = () => {
    if (!f.name.trim()) return setF({ ...f, err: "Napiš jméno nebo firmu." });
    if (f.timed && f.b - f.a < 15) return setF({ ...f, err: "Konec musí být po začátku." });
    onSave({ name: f.name.trim(), role: f.role.trim(), kind: f.kind, manual: true,
      start: f.timed ? hh(f.a) : null, end: f.timed ? hh(f.b) : null,
      hours: f.timed ? Math.round(((f.b - f.a) / 60) * 100) / 100 : (num(f.hours) || 0) });
  };
  const ph = f.kind === "kd" ? "např. investor, projektant, TDI" : f.kind === "bozp" ? "např. koordinátor BOZP, inspektor práce" : f.kind === "tdi" ? "např. TDI investora" : "např. elektrikář, revize rozvaděče";
  return (
    <SidePanel open={open} onClose={onClose} testid="manual-panel" title={`${isNew ? "Přidat" : "Upravit"} člověka mimo tým · jen do deníku`}
      footer={<>
        {!isNew && <Button variant="outline" onClick={onRemove} className="h-11 mr-auto text-destructive hover:text-destructive" data-testid="manual-panel-remove"><Trash2 size={15} /> Odstranit z deníku</Button>}
        <Button variant="outline" onClick={onClose} className="h-11 max-md:flex-1">Zrušit</Button>
        <Button onClick={save} className="h-11 max-md:flex-1" data-testid="manual-panel-save">{isNew ? "Přidat do deníku" : "Uložit"}</Button>
      </>}>
      <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted-foreground">Jméno nebo firma
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, err: "" })} placeholder="např. Elektro Novák, Ing. Malá" autoComplete="off"
          className="h-11 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-[16px] font-medium text-foreground" data-testid="manual-panel-name" /></label>
      <div>
        <div className="text-[12.5px] font-semibold text-muted-foreground mb-1.5">Kdo to je</div>
        <div className="flex flex-wrap gap-1.5">{KINDS.map(([k, l]) => <Chip key={k} on={f.kind === k} onClick={() => setF({ ...f, kind: k })} testid={`manual-panel-kind-${k}`}>{l}</Chip>)}</div>
      </div>
      <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted-foreground">Profese / poznámka
        <input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder={ph} autoComplete="off"
          className="h-11 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-[16px] font-medium text-foreground" data-testid="manual-panel-role" /></label>
      <div>
        <div className="text-[12.5px] font-semibold text-muted-foreground mb-1.5">Čas na stavbě</div>
        <div className="flex flex-wrap gap-1.5">
          <Chip on={f.timed} onClick={() => setF({ ...f, timed: true })} testid="manual-panel-timed">Od – do</Chip>
          <Chip on={!f.timed} onClick={() => setF({ ...f, timed: false })} testid="manual-panel-untimed">Bez času</Chip>
        </div>
        {f.timed && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <TimeSel label="Od" value={f.a} onChange={(a) => setF({ ...f, a })} testid="manual-panel-from" />
            <TimeSel label="Do" value={f.b} onChange={(b) => setF({ ...f, b })} testid="manual-panel-to" />
          </div>
        )}
      </div>
      <p className="text-[12.5px] text-muted-foreground">Do docházky ani do nákladů zakázky se nezapíše. Je jen v zápisu deníku a v PDF.</p>
      {f.err && <p className="text-sm font-semibold text-destructive">{f.err}</p>}
    </SidePanel>
  );
}
